import * as React from 'react';
import { Combobox as ComboboxPrimitive } from '@base-ui/react';
import {
  CheckIcon,
  ChevronDownIcon,
  CopyIcon,
  SearchIcon,
  XIcon,
} from 'lucide-react';
import useI18n from '../../hooks/useI18n';
import { cn } from '../../lib/utils';
import {
  comboboxTriggerButtonSizeVariants,
  comboboxTriggerSizeVariants,
} from '../../lib/fieldSize';
import { comboboxChipVariants } from './variants';
import { Button } from '../Button';
import { Tooltip } from '../Tooltip';
import { Label } from '../Label';
import {
  floatingFieldLabelClassName,
  floatingFieldLabelInsetClassName,
} from '../Label/helpers';
import { InputGroup, InputGroupAddon, InputGroupInput } from '../InputGroup';
import { useComboboxAnchor, useComboboxFilteredItems } from './hooks';
import {
  copyTextToClipboard,
  getComboboxItemLabel,
  getComboboxItemKey,
  hasSelectedComboboxValue,
  getComboboxGroupKey,
  shouldAnimateComboboxIndicator,
} from './helpers';
import type {
  ComboboxChipProps,
  ComboboxChipsProps,
  ComboboxClearProps,
  ComboboxCollectionProps,
  ComboboxContentProps,
  ComboboxEmptyProps,
  ComboboxGroupedItem,
  ComboboxGroupedProps,
  ComboboxGroupLabelProps,
  ComboboxGroupProps,
  ComboboxInputProps,
  ComboboxItemProps,
  ComboboxLabelProps,
  ComboboxListProps,
  ComboboxMultipleProps,
  ComboboxProps,
  ComboboxRootProps,
  ComboboxSeparatorProps,
  ComboboxTriggerButtonProps,
  ComboboxTriggerIndicatorProps,
  ComboboxTriggerProps,
} from './types';

/**
 * Groups all parts of the combobox. Doesn’t render its own HTML element.
 *
 * API:
 * - [Root API](https://base-ui.com/react/components/combobox#root)
 */
function ComboboxRoot<Value, Multiple extends boolean | undefined = false>({
  ...props
}: ComboboxRootProps<Value, Multiple>) {
  return <ComboboxPrimitive.Root {...props} />;
}

/**
 * An accessible label that is automatically associated with the combobox trigger.
 *
 * API:
 * - [Label API](https://base-ui.com/react/components/combobox#label)
 */
function ComboboxLabel({
  className,
  placement = 'inside',
  ...props
}: ComboboxLabelProps) {
  return (
    <Label
      data-slot='combobox-label'
      className={cn(
        // Base styles
        floatingFieldLabelClassName,
        'eye:mb-4',
        // Inside placement
        placement === 'inside' &&
          cn(floatingFieldLabelInsetClassName, 'eye:top-0'),
        // Outside placement
        placement === 'outside' &&
          'eye:group-has-disabled/combobox-field:text-grey-disabled eye:group-has-aria-invalid/combobox-field:text-red-100',
        // Disabled states
        'eye:group-has-disabled/combobox-trigger:text-grey-disabled',
        'eye:group-has-disabled/input-group:text-grey-disabled',
        // Validation states
        'eye:group-has-aria-invalid/combobox-trigger:text-red-100',
        'eye:group-has-[[data-slot=input-group-control][aria-invalid=true]]/input-group:text-red-100',
        className,
      )}
      {...props}
    />
  );
}

/**
 * The current value of the combobox.
 *
 * API:
 * - [Value API](https://base-ui.com/react/components/combobox#value)
 */
function ComboboxValue({ ...props }: ComboboxPrimitive.Value.Props) {
  return <ComboboxPrimitive.Value data-slot='combobox-value' {...props} />;
}

/**
 * A wrapper for the trigger button that contains the input and auxiliary
 * controls such as the clear and copy buttons. Should contain
 * `ComboboxTriggerButton` as the main interactive element that opens the popup.
 */
function ComboboxTriggerInner<SelectedValue, CopyValue = SelectedValue>(
  {
    animateIndicator = true,
    className,
    children,
    disabled = false,
    showClear = false,
    showCopy = false,
    size,
    style,
    customTriggers,
    onCopyAccessor,
    ...props
  }: ComboboxTriggerProps<SelectedValue, CopyValue>,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const { t } = useI18n();
  let padding = 52;

  if (showClear && showCopy) {
    padding = 108;
  } else if (showClear || showCopy) {
    padding = 80;
  }

  if (customTriggers?.length) {
    padding += customTriggers.reduce(
      (sum, trigger) => sum + (trigger.reservedWidth ?? 28),
      0,
    );
  }

  return (
    <InputGroup
      ref={ref}
      data-slot='combobox-trigger'
      className={cn(
        'eye:group/combobox-trigger',
        comboboxTriggerSizeVariants({ size }),
        className,
      )}
      style={
        {
          ...style,
          '--combobox-trigger-end-padding': `${padding}px`,
        } as React.CSSProperties
      }
      {...props}
    >
      {children}
      <InputGroupAddon
        align='inline-end'
        className='eye:pointer-events-none eye:absolute eye:inset-y-0 eye:right-0 eye:z-10 eye:cursor-default eye:gap-4 eye:self-stretch eye:pr-16!'
      >
        {showClear && <ComboboxClear aria-label={t('clearValue')} />}
        {showCopy && (
          <ComboboxTriggerCopy<SelectedValue, CopyValue>
            disabled={disabled}
            onCopyAccessor={onCopyAccessor}
          />
        )}
        {customTriggers?.map((trigger) => (
          <ComboboxValue key={trigger.key}>
            {(selectedValue: SelectedValue) => trigger.render(selectedValue)}
          </ComboboxValue>
        ))}
        <ComboboxTriggerIndicator animate={animateIndicator} />
      </InputGroupAddon>
    </InputGroup>
  );
}

type ComboboxTriggerComponent = <SelectedValue, CopyValue = SelectedValue>(
  props: ComboboxTriggerProps<SelectedValue, CopyValue> &
    React.RefAttributes<HTMLDivElement>,
) => React.ReactElement | null;

const ComboboxTriggerBase = React.forwardRef(ComboboxTriggerInner);

ComboboxTriggerBase.displayName = 'ComboboxTrigger';

const ComboboxTrigger =
  ComboboxTriggerBase as unknown as ComboboxTriggerComponent;

/**
 * A button that copies the current value to the clipboard. Should be used within `ComboboxTrigger`.
 */
function ComboboxTriggerCopy<SelectedValue, CopyValue = SelectedValue>({
  className,
  onClick,
  onMouseDown,
  onPointerDown,
  onCopyAccessor,
  ...props
}: React.ComponentPropsWithoutRef<typeof Button> & {
  onCopyAccessor?: ComboboxTriggerProps<
    SelectedValue,
    CopyValue
  >['onCopyAccessor'];
}) {
  const { t } = useI18n();

  return (
    <ComboboxValue>
      {(selectedValue: SelectedValue) => {
        if (!hasSelectedComboboxValue(selectedValue)) {
          return null;
        }

        return (
          <Tooltip content={t('copyToClipboard')} delayDuration={1500}>
            <Button
              variant='ghost'
              size='icon-xs'
              type='button'
              data-slot='combobox-trigger-copy'
              aria-label={t('copyToClipboard')}
              className={cn(
                'eye:group/combobox-trigger-copy eye:pointer-events-auto eye:shrink-0 eye:cursor-pointer eye:hover:bg-grey-20 eye:disabled:cursor-not-allowed eye:disabled:bg-transparent eye:disabled:text-grey-disabled eye:disabled:hover:bg-transparent eye:data-pressed:bg-transparent',
                className,
              )}
              onPointerDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onPointerDown?.(event);
              }}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onMouseDown?.(event);
              }}
              onClick={async (event) => {
                event.stopPropagation();

                try {
                  let text = '';
                  if (typeof onCopyAccessor === 'function') {
                    text =
                      onCopyAccessor(selectedValue as unknown as CopyValue) ||
                      '';
                  } else if (
                    selectedValue &&
                    typeof selectedValue === 'object' &&
                    'label' in selectedValue
                  ) {
                    text = (selectedValue?.label as string) || '';
                  }
                  await copyTextToClipboard(text);
                } finally {
                  onClick?.(event);
                }
              }}
              {...props}
            >
              <CopyIcon
                data-slot='combobox-trigger-copy-icon'
                className='eye:pointer-events-none eye:size-16 eye:shrink-0'
              />
            </Button>
          </Tooltip>
        );
      }}
    </ComboboxValue>
  );
}

/**
 * A button that opens the popup. Should be used within `ComboboxTrigger` as the main interactive element that opens the popup.
 *
 * API:
 * - [Trigger API](https://base-ui.com/react/components/combobox#trigger)
 */
const ComboboxTriggerButton = React.forwardRef<
  HTMLButtonElement,
  ComboboxTriggerButtonProps
>(({ className, children, size, style, ...props }, ref) => {
  return (
    <ComboboxPrimitive.Trigger
      ref={ref}
      render={<button type='button' />}
      data-slot='combobox-trigger-button'
      className={cn(
        'eye:group/combobox-trigger-button eye:min-h-46 eye:flex eye:min-w-0 eye:flex-1 eye:cursor-pointer eye:items-center eye:gap-8 eye:self-stretch eye:border-none eye:bg-transparent eye:py-0 eye:pr-16 eye:pl-16 eye:text-left eye:text-16 eye:text-black eye:outline-none eye:disabled:cursor-not-allowed eye:disabled:text-grey-disabled',
        'eye:has-data-[slot=combobox-chip]:py-8',
        comboboxTriggerButtonSizeVariants({ size }),
        className,
      )}
      style={
        {
          paddingRight: 'var(--combobox-trigger-end-padding, 16px)',
          ...style,
        } as React.CSSProperties
      }
      {...props}
    >
      <span className='eye:flex eye:min-w-0 eye:flex-1 eye:items-center eye:gap-8'>
        {children}
      </span>
    </ComboboxPrimitive.Trigger>
  );
});

ComboboxTriggerButton.displayName = 'ComboboxTriggerButton';

/**
 * A decorative chevron rendered inside the trigger action area.
 * The actual toggle interaction stays on the single Base UI trigger button.
 */
function ComboboxTriggerIndicator({
  animate = true,
  className,
  ...props
}: ComboboxTriggerIndicatorProps) {
  return (
    <span
      data-slot='combobox-trigger-indicator'
      aria-hidden='true'
      className={cn(
        'eye:pointer-events-none eye:flex eye:size-24 eye:shrink-0 eye:items-center eye:justify-center eye:rounded-[3px]',
        className,
      )}
      {...props}
    >
      <ChevronDownIcon
        data-slot='combobox-trigger-icon'
        className={cn(
          'eye:pointer-events-none eye:size-20 eye:shrink-0 eye:transform-gpu eye:text-grey-120 eye:will-change-transform eye:group-has-disabled/combobox-trigger:text-grey-disabled eye:group-has-data-popup-open/combobox-trigger:rotate-180',
          animate && 'eye:transition-transform eye:duration-150',
        )}
      />
    </span>
  );
}

/**
 * A container for the chips in a multiselectable input
 *
 * API:
 * - [Chips API](https://base-ui.com/react/components/combobox#chips)
 */
const ComboboxChips = React.forwardRef<HTMLDivElement, ComboboxChipsProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <ComboboxPrimitive.Chips
        ref={ref}
        data-slot='combobox-chips'
        className={cn(
          'eye:flex eye:max-w-full eye:min-w-0 eye:flex-wrap eye:gap-[6px]',
          className,
        )}
        {...props}
      >
        {children}
      </ComboboxPrimitive.Chips>
    );
  },
);

ComboboxChips.displayName = 'ComboboxChips';

/**
 * An individual chip that represents a value in a multiselectable input.
 */
function ComboboxChip({ className, variant, ...props }: ComboboxChipProps) {
  return (
    <span
      data-slot='combobox-chip'
      className={cn(comboboxChipVariants({ variant }), className)}
      {...props}
    />
  );
}

/**
 * API:
 * - [Clear API](https://base-ui.com/react/components/combobox#clear)
 */
function ComboboxClear({ className, ...props }: ComboboxClearProps) {
  return (
    <ComboboxPrimitive.Clear
      data-slot='combobox-clear'
      render={<Button variant='ghost' size='icon-xs' />}
      className={cn('eye:pointer-events-auto', className)}
      {...props}
    >
      <XIcon className='eye:pointer-events-none eye:size-16' />
    </ComboboxPrimitive.Clear>
  );
}

/**
 * API:
 * - [InputGroup API](https://base-ui.com/react/components/combobox#input-group)
 * - [Input API](https://base-ui.com/react/components/combobox#input)
 */
function ComboboxInput({
  className,
  placeholder,
  ...props
}: ComboboxInputProps) {
  const { t } = useI18n();

  return (
    <ComboboxPrimitive.InputGroup
      render={<InputGroup />}
      data-slot='combobox-input-group'
      className={cn(
        'eye:rounded-b-none eye:border-0! eye:border-b! eye:border-b-grey-40! eye:outline-none! eye:focus-within:border-b-grey-40! eye:hover:border-b-grey-40!',
        'eye:*:data-[slot=input-group-control]:px-16',
        'eye:group-data-closed/combobox-content:**:data-[slot=input-group-control]:text-transparent eye:group-data-closed/combobox-content:**:data-[slot=input-group-control]:caret-transparent',
        className,
      )}
    >
      <InputGroupAddon
        align='inline-start'
        className='eye:pointer-events-none eye:cursor-default'
      >
        <SearchIcon
          data-slot='combobox-input-icon'
          className='eye:size-16 eye:text-grey-100'
        />
      </InputGroupAddon>
      <ComboboxPrimitive.Input
        render={<InputGroupInput className='eye:px-16' />}
        // A placeholder is not an accessible name, and Base UI only derives one
        // from its own Field label, which the wrappers do not use.
        aria-label={t('search')}
        placeholder={placeholder ?? t('searchPlaceholder')}
        {...props}
      />
    </ComboboxPrimitive.InputGroup>
  );
}

/**
 * API:
 * - [Portal API](https://base-ui.com/react/components/combobox#portal)
 * - [Positioner API](https://base-ui.com/react/components/combobox#positioner)
 * - [Popup API](https://base-ui.com/react/components/combobox#popup)
 */
function ComboboxContent({
  className,
  container,
  side = 'bottom',
  sideOffset = 6,
  align = 'start',
  alignOffset = 0,
  anchor,
  ...props
}: ComboboxContentProps) {
  return (
    // Portaled to document.body by default. The popup uses z-1400 so it
    // appears above the Dialog overlay (z-1300) but below Tooltip (z-1500).
    // Override `container` when rendering inside a stacking context that
    // needs the popup scoped to it, or override z-index via CSS.
    <ComboboxPrimitive.Portal container={container}>
      <ComboboxPrimitive.Positioner
        data-slot='combobox-positioner'
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        anchor={anchor}
        className='eye:isolate eye:z-1400'
      >
        <ComboboxPrimitive.Popup
          data-slot='combobox-content'
          className={cn(
            // Layout and sizing
            'eye:group/combobox-content eye:relative eye:z-1410 eye:box-border',
            'eye:w-(--anchor-width) eye:overflow-hidden',
            // Surface
            'eye:rounded-md eye:border eye:border-grey-40 eye:bg-white eye:text-black',
            'eye:shadow-popover-content',
            // Motion
            'eye:duration-100 eye:data-closed:animate-out eye:data-closed:fade-out-0 eye:data-closed:zoom-out-95',
            'eye:data-open:animate-in eye:data-open:fade-in-0 eye:data-open:zoom-in-95',
            'eye:data-[side=bottom]:slide-in-from-top-2 eye:data-[side=left]:slide-in-from-right-2 eye:data-[side=right]:slide-in-from-left-2 eye:data-[side=top]:slide-in-from-bottom-2',
            className,
          )}
          {...props}
        />
      </ComboboxPrimitive.Positioner>
    </ComboboxPrimitive.Portal>
  );
}

/**
 * API:
 * - [List API](https://base-ui.com/react/components/combobox#list)
 */
function ComboboxList({ className, ...props }: ComboboxListProps) {
  return (
    <ComboboxPrimitive.List
      data-slot='combobox-list'
      className={cn(
        'eye:max-h-[324px] eye:scroll-py-4 eye:overflow-y-auto eye:py-4xs eye:data-empty:p-0',
        className,
      )}
      {...props}
    />
  );
}

/**
 * API:
 * - [Item API](https://base-ui.com/react/components/combobox#item)
 */
const ComboboxItem = React.forwardRef<HTMLDivElement, ComboboxItemProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <ComboboxPrimitive.Item
        ref={ref}
        data-slot='combobox-item'
        className={cn(
          // Layout
          'eye:flex eye:min-h-[44px] eye:w-full eye:items-center eye:gap-8 eye:border-0 eye:px-16 eye:py-[6px]',
          // Typography and transitions
          'eye:text-14 eye:text-black eye:transition-[background-color,color] eye:duration-150',
          // Interaction
          'eye:cursor-pointer eye:outline-none eye:select-none',
          'eye:hover:bg-grey-10 eye:hover:text-black eye:focus:outline-none eye:focus-visible:outline-none',
          // Stateful variants
          'eye:data-disabled:pointer-events-none eye:data-disabled:opacity-50',
          'eye:data-highlighted:bg-grey-10 eye:data-highlighted:text-black',
          // Shape, not colour alone: the tinted background is far below 3:1.
          'eye:data-highlighted:inset-ring-2 eye:data-highlighted:inset-ring-black',
          'eye:data-selected:bg-grey-20 eye:data-selected:text-black eye:data-selected:hover:bg-grey-40',
          'eye:[&[data-selected][data-highlighted]]:bg-grey-20 eye:[&[data-selected][data-highlighted]]:text-black eye:[&[data-selected][data-highlighted]:hover]:bg-grey-40',
          className,
        )}
        {...props}
      >
        <span className='eye:min-w-0 eye:flex-1'>{children}</span>
        <ComboboxPrimitive.ItemIndicator
          data-slot='combobox-item-indicator'
          className='eye:ml-auto eye:flex eye:shrink-0 eye:items-center'
        >
          <CheckIcon aria-hidden='true' className='eye:size-16' />
        </ComboboxPrimitive.ItemIndicator>
      </ComboboxPrimitive.Item>
    );
  },
);

ComboboxItem.displayName = 'ComboboxItem';

/**
 * API:
 * - [Group API](https://base-ui.com/react/components/combobox#group)
 */
function ComboboxGroup({ className, ...props }: ComboboxGroupProps) {
  return (
    <ComboboxPrimitive.Group
      data-slot='combobox-group'
      className={cn(className)}
      {...props}
    />
  );
}

/**
 * API:
 * - [GroupLabel API](https://base-ui.com/react/components/combobox#group-label)
 */
function ComboboxGroupLabel({ className, ...props }: ComboboxGroupLabelProps) {
  return (
    <ComboboxPrimitive.GroupLabel
      data-slot='combobox-group-label'
      className={cn(
        'eye:px-16 eye:py-[6px] eye:text-12 eye:text-grey-120!',
        className,
      )}
      {...props}
    />
  );
}

/**
 * API:
 * - [Collection API](https://base-ui.com/react/components/combobox#collection)
 */
function ComboboxCollection({ ...props }: ComboboxCollectionProps) {
  return (
    <ComboboxPrimitive.Collection data-slot='combobox-collection' {...props} />
  );
}

/**
 * API:
 * - [Empty API](https://base-ui.com/react/components/combobox#empty)
 */
function ComboboxEmpty({ children, className, ...props }: ComboboxEmptyProps) {
  const { t } = useI18n();

  return (
    <ComboboxPrimitive.Empty
      data-slot='combobox-empty'
      className={cn(
        'eye:body-m eye:hidden eye:min-h-[44px] eye:w-full eye:items-center eye:justify-center eye:px-16 eye:text-center eye:text-grey-120 eye:group-data-empty/combobox-content:flex',
        className,
      )}
      {...props}
    >
      {children ?? t('noResultsFound')}
    </ComboboxPrimitive.Empty>
  );
}

/**
 * API:
 * - [Separator API](https://base-ui.com/react/components/combobox#separator)
 */
function ComboboxSeparator({ className, ...props }: ComboboxSeparatorProps) {
  return (
    <ComboboxPrimitive.Separator
      data-slot='combobox-separator'
      className={cn('eye:my-4 eye:h-px eye:bg-grey-20', className)}
      {...props}
    />
  );
}

/**
 * A list container for grouped items.
 */
function ComboboxGroupedList<Value>({
  itemToStringLabel,
  itemToStringValue,
  renderGroupLabel,
  renderItemLabel,
  slotProps,
}: {
  itemToStringLabel: ComboboxGroupedProps<Value>['itemToStringLabel'];
  itemToStringValue: ComboboxGroupedProps<Value>['itemToStringValue'];
  renderGroupLabel: ComboboxGroupedProps<Value>['renderGroupLabel'];
  renderItemLabel: ComboboxGroupedProps<Value>['renderItemLabel'];
  slotProps: ComboboxGroupedProps<Value>['slotProps'];
}) {
  const filteredGroups = useComboboxFilteredItems<ComboboxGroupedItem<Value>>();
  const visibleGroupCount = filteredGroups.length;

  return (
    <ComboboxList>
      {(group: ComboboxGroupedItem<Value>, groupIndex: number) => {
        const groupKey = getComboboxGroupKey(group, groupIndex);

        return (
          <ComboboxGroup
            {...slotProps?.group}
            key={groupKey}
            items={group.items}
          >
            <ComboboxGroupLabel
              {...slotProps?.groupLabel}
              key={`${groupKey}-label`}
            >
              {renderGroupLabel?.(group) ?? group.value}
            </ComboboxGroupLabel>
            <ComboboxCollection key={`${groupKey}-items`}>
              {(item: Value, itemIndex: number) => (
                <ComboboxItem
                  {...slotProps?.item}
                  key={`${groupKey}-${getComboboxItemKey(
                    item,
                    itemIndex,
                    itemToStringValue,
                  )}`}
                  value={item}
                >
                  {renderItemLabel?.(item) ??
                    getComboboxItemLabel(item, itemToStringLabel)}
                </ComboboxItem>
              )}
            </ComboboxCollection>
            {groupIndex < visibleGroupCount - 1 && (
              <ComboboxSeparator
                {...slotProps?.separator}
                key={`${groupKey}-separator`}
              />
            )}
          </ComboboxGroup>
        );
      }}
    </ComboboxList>
  );
}

/**
 * A light wrapper around the composable Base UI Combobox components that supports single selection mode. Use `ComboboxMultiple` for multiple selection mode.
 *
 * Demo:
 * - [Combobox](https://base-ui.com/react/components/combobox)
 */
function Combobox<Value>({
  disabled = false,
  items,
  itemToStringLabel,
  itemToStringValue,
  label,
  noItemsText,
  renderItemLabel,
  renderValue,
  required,
  size,
  slotProps,
  ...rest
}: ComboboxProps<Value>) {
  const anchor = useComboboxAnchor();
  const generatedTriggerId = React.useId();
  const shouldAnimateIndicator = shouldAnimateComboboxIndicator(
    items?.length ?? 0,
  );

  const {
    id: triggerIdProp,
    'aria-label': triggerAriaLabel,
    'aria-labelledby': triggerAriaLabelledby,
    'aria-describedby': triggerAriaDescribedby,
    customTriggers,
    onCopyAccessor,
    ...triggerProps
  } = slotProps?.trigger ?? {};

  const triggerId = triggerIdProp ?? generatedTriggerId;
  const hasLabel = label != null;
  const labelPlacement = slotProps?.label?.placement ?? 'inside';
  const labelId = hasLabel ? `${triggerId}-label` : undefined;

  return (
    <ComboboxRoot<Value>
      disabled={disabled}
      items={items}
      required={required}
      itemToStringLabel={itemToStringLabel}
      itemToStringValue={itemToStringValue}
      autoHighlight
      {...rest}
    >
      <div className='eye:group/combobox-field eye:block eye:w-full'>
        {hasLabel && labelPlacement === 'outside' && (
          <ComboboxLabel
            {...slotProps?.label}
            id={labelId}
            htmlFor={triggerId}
            placement='outside'
          >
            {required ? `${label} *` : label}
          </ComboboxLabel>
        )}
        <ComboboxTrigger
          ref={anchor}
          animateIndicator={shouldAnimateIndicator}
          disabled={disabled}
          customTriggers={customTriggers}
          onCopyAccessor={onCopyAccessor}
          size={size}
          {...triggerProps}
        >
          {hasLabel && labelPlacement === 'inside' && (
            <ComboboxLabel
              {...slotProps?.label}
              id={labelId}
              htmlFor={triggerId}
              placement='inside'
            >
              {required ? `${label} *` : label}
            </ComboboxLabel>
          )}
          <ComboboxTriggerButton
            id={triggerId}
            size={size}
            aria-invalid={triggerProps['aria-invalid']}
            aria-label={triggerAriaLabel}
            aria-labelledby={
              triggerAriaLabelledby ??
              (!triggerAriaLabel && labelId ? labelId : undefined)
            }
            aria-describedby={triggerAriaDescribedby}
          >
            <ComboboxValue {...slotProps?.value}>
              {(selectedValue: Value | null) => (
                <span
                  className={cn(
                    'eye:truncate',
                    !selectedValue && 'eye:text-grey-placeholder',
                  )}
                >
                  {selectedValue
                    ? (renderValue?.(selectedValue) ??
                      getComboboxItemLabel(selectedValue, itemToStringLabel))
                    : slotProps?.value?.placeholder}
                </span>
              )}
            </ComboboxValue>
          </ComboboxTriggerButton>
        </ComboboxTrigger>
      </div>
      <ComboboxContent anchor={anchor} {...slotProps?.content}>
        <ComboboxInput {...slotProps?.searchInput} />
        <ComboboxEmpty {...slotProps?.empty}>{noItemsText}</ComboboxEmpty>
        <ComboboxList>
          <ComboboxCollection>
            {(item: Value, index: number) => (
              <ComboboxItem
                {...slotProps?.item}
                key={getComboboxItemKey(item, index, itemToStringValue)}
                value={item}
              >
                {renderItemLabel?.(item) ??
                  getComboboxItemLabel(item, itemToStringLabel)}
              </ComboboxItem>
            )}
          </ComboboxCollection>
        </ComboboxList>
      </ComboboxContent>
    </ComboboxRoot>
  );
}

/**
 * A light wrapper around the composable Base UI Combobox components that supports grouped single selection mode.
 *
 * Demo:
 * - [Combobox](https://base-ui.com/react/components/combobox)
 */
function ComboboxGrouped<Value>({
  disabled = false,
  items,
  itemToStringLabel,
  itemToStringValue,
  label,
  noItemsText,
  required,
  renderGroupLabel,
  renderItemLabel,
  size,
  slotProps,
  ...rest
}: ComboboxGroupedProps<Value>) {
  const anchor = useComboboxAnchor();
  const generatedTriggerId = React.useId();
  const shouldAnimateIndicator = shouldAnimateComboboxIndicator(
    items?.reduce((total, group) => total + group.items.length, 0) ?? 0,
  );

  const {
    id: triggerIdProp,
    'aria-label': triggerAriaLabel,
    'aria-labelledby': triggerAriaLabelledby,
    'aria-describedby': triggerAriaDescribedby,
    customTriggers,
    onCopyAccessor,
    ...triggerProps
  } = slotProps?.trigger ?? {};

  const triggerId = triggerIdProp ?? generatedTriggerId;
  const hasLabel = label != null;
  const labelPlacement = slotProps?.label?.placement ?? 'inside';
  const labelId = hasLabel ? `${triggerId}-label` : undefined;

  return (
    <ComboboxRoot<Value>
      disabled={disabled}
      items={items}
      required={required}
      itemToStringLabel={itemToStringLabel}
      itemToStringValue={itemToStringValue}
      autoHighlight
      {...rest}
    >
      <div className='eye:group/combobox-field eye:block eye:w-full'>
        {hasLabel && labelPlacement === 'outside' && (
          <ComboboxLabel
            {...slotProps?.label}
            id={labelId}
            htmlFor={triggerId}
            placement='outside'
          >
            {required ? `${label} *` : label}
          </ComboboxLabel>
        )}
        <ComboboxTrigger
          ref={anchor}
          animateIndicator={shouldAnimateIndicator}
          disabled={disabled}
          customTriggers={customTriggers}
          onCopyAccessor={onCopyAccessor}
          size={size}
          {...triggerProps}
        >
          {hasLabel && labelPlacement === 'inside' && (
            <ComboboxLabel
              {...slotProps?.label}
              id={labelId}
              htmlFor={triggerId}
              placement='inside'
            >
              {required ? `${label} *` : label}
            </ComboboxLabel>
          )}
          <ComboboxTriggerButton
            id={triggerId}
            size={size}
            aria-invalid={triggerProps['aria-invalid']}
            aria-label={triggerAriaLabel}
            aria-labelledby={
              triggerAriaLabelledby ??
              (!triggerAriaLabel && labelId ? labelId : undefined)
            }
            aria-describedby={triggerAriaDescribedby}
          >
            <ComboboxValue {...slotProps?.value}>
              {(selectedValue: Value | null) => (
                <span
                  className={cn(
                    'eye:truncate',
                    !selectedValue && 'eye:text-grey-placeholder',
                  )}
                >
                  {selectedValue
                    ? getComboboxItemLabel(selectedValue, itemToStringLabel)
                    : slotProps?.value?.placeholder}
                </span>
              )}
            </ComboboxValue>
          </ComboboxTriggerButton>
        </ComboboxTrigger>
      </div>
      <ComboboxContent anchor={anchor} {...slotProps?.content}>
        <ComboboxInput {...slotProps?.searchInput} />
        <ComboboxEmpty {...slotProps?.empty}>{noItemsText}</ComboboxEmpty>
        <ComboboxGroupedList
          itemToStringLabel={itemToStringLabel}
          itemToStringValue={itemToStringValue}
          renderGroupLabel={renderGroupLabel}
          renderItemLabel={renderItemLabel}
          slotProps={slotProps}
        />
      </ComboboxContent>
    </ComboboxRoot>
  );
}

/**
 * A light wrapper around the composable Base UI Combobox components that supports multiple selection mode. Use `Combobox` for single selection mode.
 *
 * Demo:
 * - [Combobox](https://base-ui.com/react/components/combobox)
 */
function ComboboxMultiple<Value>({
  disabled = false,
  items,
  label,
  noItemsText,
  renderChipLabel,
  renderItemLabel,
  required,
  size,
  slotProps,
  itemToStringLabel,
  itemToStringValue,
  ...rest
}: ComboboxMultipleProps<Value>) {
  const anchor = useComboboxAnchor();
  const generatedTriggerId = React.useId();
  const shouldAnimateIndicator = shouldAnimateComboboxIndicator(
    items?.length ?? 0,
  );

  const {
    id: triggerIdProp,
    'aria-label': triggerAriaLabel,
    'aria-labelledby': triggerAriaLabelledby,
    'aria-describedby': triggerAriaDescribedby,
    customTriggers,
    onCopyAccessor,
    ...triggerProps
  } = slotProps?.trigger ?? {};

  const triggerId = triggerIdProp ?? generatedTriggerId;
  const hasLabel = label != null;
  const labelPlacement = slotProps?.label?.placement ?? 'inside';
  const labelId = hasLabel ? `${triggerId}-label` : undefined;

  return (
    <ComboboxRoot<Value, true>
      disabled={disabled}
      items={items}
      required={required}
      itemToStringLabel={itemToStringLabel}
      itemToStringValue={itemToStringValue}
      multiple
      autoHighlight
      {...rest}
    >
      <div className='eye:group/combobox-field eye:block eye:w-full'>
        {hasLabel && labelPlacement === 'outside' && (
          <ComboboxLabel
            {...slotProps?.label}
            id={labelId}
            htmlFor={triggerId}
            placement='outside'
          >
            {required ? `${label} *` : label}
          </ComboboxLabel>
        )}
        <ComboboxTrigger
          ref={anchor}
          animateIndicator={shouldAnimateIndicator}
          disabled={disabled}
          customTriggers={customTriggers}
          onCopyAccessor={onCopyAccessor}
          size={size}
          {...triggerProps}
        >
          {hasLabel && labelPlacement === 'inside' && (
            <ComboboxLabel
              {...slotProps?.label}
              id={labelId}
              htmlFor={triggerId}
              placement='inside'
            >
              {required ? `${label} *` : label}
            </ComboboxLabel>
          )}
          <ComboboxTriggerButton
            id={triggerId}
            size={size}
            aria-invalid={triggerProps['aria-invalid']}
            aria-label={triggerAriaLabel}
            aria-labelledby={
              triggerAriaLabelledby ??
              (!triggerAriaLabel && labelId ? labelId : undefined)
            }
            aria-describedby={triggerAriaDescribedby}
          >
            <ComboboxValue {...slotProps?.value}>
              {(selectedValue: Value[] | null) => {
                if (!selectedValue?.length) {
                  return (
                    <span className='eye:truncate eye:text-grey-placeholder'>
                      {slotProps?.value?.placeholder}
                    </span>
                  );
                }

                return (
                  <ComboboxChips>
                    {selectedValue.map((selectedItem, index) => (
                      <ComboboxChip
                        {...slotProps?.chip}
                        key={getComboboxItemKey(
                          selectedItem,
                          index,
                          itemToStringValue,
                        )}
                      >
                        {renderChipLabel?.(selectedItem) ??
                          getComboboxItemLabel(selectedItem, itemToStringLabel)}
                      </ComboboxChip>
                    ))}
                  </ComboboxChips>
                );
              }}
            </ComboboxValue>
          </ComboboxTriggerButton>
        </ComboboxTrigger>
      </div>
      <ComboboxContent anchor={anchor} {...slotProps?.content}>
        <ComboboxInput {...slotProps?.searchInput} />
        <ComboboxEmpty {...slotProps?.empty}>{noItemsText}</ComboboxEmpty>
        <ComboboxList>
          <ComboboxCollection>
            {(item: Value, index: number) => (
              <ComboboxItem
                {...slotProps?.item}
                key={getComboboxItemKey(item, index, itemToStringValue)}
                value={item}
              >
                {renderItemLabel?.(item) ??
                  getComboboxItemLabel(item, itemToStringLabel)}
              </ComboboxItem>
            )}
          </ComboboxCollection>
        </ComboboxList>
      </ComboboxContent>
    </ComboboxRoot>
  );
}

export {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxClear,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGrouped,
  ComboboxGroup,
  ComboboxGroupLabel,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  ComboboxMultiple,
  ComboboxRoot,
  ComboboxSeparator,
  ComboboxTrigger,
  ComboboxTriggerButton,
  ComboboxTriggerIndicator,
  ComboboxValue,
};
